'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Hl, Lesson, M, MB, NumberQuiz, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/prob-extra'

const BayesGrid = dynamic(() => import('../_components/widgets/ProbExtraWidgets').then(m => m.BayesGrid), { ssr: false })

export default function ConditionalAndBayes() {
  return (
    <Lesson>
      <Step>
        <H>Zooming in</H>
        <P>
          Last lesson ended with conditional probability: <M>{'P(\\text{rain} \\mid \\text{cloudy})'}</M>, the chance of rain{' '}
          <em>given</em> that it’s cloudy. Here’s how to compute one. Zoom in on the cloudy days only, forget the rest, and
          count.
        </P>
        <MB>{'P(A \\mid B) = \\frac{P(A \\text{ and } B)}{P(B)}'}</MB>
        <Exercise id="cond-table" title="Counting with a condition" {...EX.condTable} />
        <Callout kind="warn">
          <M>{'P(A \\mid B)'}</M> and <M>{'P(B \\mid A)'}</M> are different numbers. Most rainy days are cloudy, but most cloudy
          days aren’t rainy. Mixing them up is one of the most common mistakes in all of reasoning. It even has a name: the{' '}
          <em>prosecutor’s fallacy</em>.
        </Callout>
      </Step>

      <Step gate="q-test">
        <H>The test that’s usually wrong</H>
        <P>
          A disease affects 1 in 100 people. A test for it catches 90% of sick people, but also gives a false alarm for 9% of
          healthy ones. You test positive. How worried should you be? Guess first, then play with the population below.
        </P>
        <Figure n="5.3" wide caption="1 000 people. Tick ‘only show positive tests’ and count: the false alarms from the huge healthy group outnumber the true cases from the small sick group.">
          <BayesGrid />
        </Figure>
        <Quiz
          id="q-test"
          question="With 1% sick, 90% caught and 9% false alarms: if you test positive, roughly what’s the chance you’re sick?"
          options={[
            { label: 'About 9%', correct: true },
            { label: 'About 90%', why: 'That’s P(positive | sick). The question asks the reverse: P(sick | positive).' },
            { label: 'About 1%', why: 'The positive test is real evidence. It raises the chance from 1% — just not as far as you’d think.' },
          ]}
          explain="9 true cases versus about 89 false alarms: 9 / 98 ≈ 9%. Rare things stay fairly unlikely even after decent evidence. Doctors, judges and AI systems all have to get this right."
        />
      </Step>

      <Step gate={['nq-bayes', 'bayes-rule']}>
        <H>Bayes’ rule</H>
        <P>
          The grid is doing a calculation, and it has a famous name. <strong>Bayes’ rule</strong> turns{' '}
          <M>{'P(\\text{evidence} \\mid \\text{cause})'}</M>, which is easy to measure, into{' '}
          <M>{'P(\\text{cause} \\mid \\text{evidence})'}</M>, which is what you actually want to know:
        </P>
        <MB>{'\\underbrace{P(\\text{sick} \\mid +)}_{\\text{posterior}} = \\frac{\\overbrace{P(+ \\mid \\text{sick})}^{\\text{likelihood}}\\;\\overbrace{P(\\text{sick})}^{\\text{prior}}}{P(+)}'}</MB>
        <P>
          The <Hl c="violet">prior</Hl> is what you believed before the evidence. The <Hl c="blue">likelihood</Hl> is how well
          each explanation predicts the evidence. The <Hl c="accent">posterior</Hl> is your updated belief. The bottom,{' '}
          <M>{'P(+)'}</M>, adds up every way of testing positive: sick and caught, or healthy and a false alarm.
        </P>
        <NumberQuiz
          id="nq-bayes"
          question={
            <>
              Prior 50%, the test catches 80% of cases, and false alarms happen 20% of the time. What is{' '}
              <M>{'P(\\text{sick} \\mid +)'}</M>? (Answer as a decimal.)
            </>
          }
          answer={0.8}
          tolerance={0.005}
          explain={<>Top: 0.8 × 0.5 = 0.4. Bottom: 0.4 + 0.2 × 0.5 = 0.5. So 0.4 / 0.5 = 0.8.</>}
          hint={<>Top: P(+ | sick) × P(sick). Bottom: the top plus P(+ | healthy) × P(healthy).</>}
        />
        <Exercise
          id="bayes-rule"
          title="posterior and afterTests"
          {...EX.bayesRule}
          hints={[
            <>
              <C>{'const pPositive = sensitivity * prior + falseAlarm * (1 - prior)'}</C>, then divide{' '}
              <C>sensitivity * prior</C> by it.
            </>,
            <>
              afterTests: <C>{'let p = prior'}</C>, then <C>k</C> times <C>{'p = posterior(p, sensitivity, falseAlarm)'}</C>.
            </>,
          ]}
        />
        <Callout kind="plain">
          Today’s posterior is tomorrow’s prior. Each new piece of evidence nudges your beliefs, and that’s Bayes’ rule as a way
          of learning.
        </Callout>
      </Step>

      <Step gate={['spam-word', 'q-independent']}>
        <H>Your first spam detector</H>
        <P>
          Swap “sick” for “spam” and “positive test” for “contains the word <em>free</em>”, and you have the beginnings of a
          spam filter. Count how often the word appears in spam and in normal email, then apply Bayes:
        </P>
        <Exercise
          id="spam-word"
          title="P(spam | word)"
          {...EX.spamWord}
          hints={[
            <>
              <C>{'const group = emails.filter(e => e.spam === spam)'}</C>, then divide the number in <C>group</C> that contain the
              word by <C>group.length</C>.
            </>,
            <>
              Prior <C>pSpam</C> is the fraction of all emails that are spam. Then it’s the same formula as{' '}
              <C>posterior</C>.
            </>,
          ]}
        />
        <P>
          Two events are <strong>independent</strong> if knowing one tells you nothing about the other:{' '}
          <M>{'P(A \\mid B) = P(A)'}</M>. Then, and only then, <M>{'P(A \\text{ and } B) = P(A)\\,P(B)'}</M>.
        </P>
        <Quiz
          id="q-independent"
          question="Are the words “click” and “here” independent in emails?"
          options={[
            { label: 'Probably not — seeing one makes the other more likely', correct: true },
            { label: 'Yes, words are always independent', why: 'Words come in phrases. “click” is often followed by “here”.' },
            { label: 'Yes, because they’re different words', why: 'Being different doesn’t make them independent. Independence is about whether they tell you about each other.' },
          ]}
          explain="Words aren’t independent. But in the project at the end of this module you’ll pretend they are, and the ‘naive’ filter still works remarkably well."
        />
      </Step>
    </Lesson>
  )
}
